import React from "react";
import { useLocation, Link } from "react-router-dom";
import { NAV_GROUPS, MODULE_META } from "@/config/navigation";
import { Search, ChevronRight, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNavigate } from "react-router-dom";

function Breadcrumbs() {
    const location = useLocation();
    const parts = location.pathname.split("/").filter(Boolean);
    // Strip the "/app" prefix so breadcrumbs reflect the module segment.
    const segment = parts[0] === "app" ? parts[1] : parts[0];
    const all = NAV_GROUPS.flatMap((g) => g.items);
    const current = all.find((i) => i.path === "/app/" + segment);
    const label = current?.label || MODULE_META[segment]?.title || "Workspace";

    return (
        <div
            data-testid="breadcrumbs"
            className="flex items-center gap-1.5 text-sm text-zinc-500"
        >
            <span>Yug Traders</span>
            <ChevronRight className="h-3.5 w-3.5 text-zinc-300" strokeWidth={1.5} />
            <span className="text-zinc-900 font-medium">{label}</span>
        </div>
    );
}

function GlobalSearch({ onOpen }) {
    return (
        <button
            data-testid="global-search-trigger"
            onClick={onOpen}
            className="group flex h-8 items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 text-left text-sm text-zinc-500 hover:border-zinc-300 hover:bg-zinc-50 transition-colors min-w-[280px]"
        >
            <Search className="h-3.5 w-3.5" strokeWidth={1.5} />
            <span>Search anything…</span>
            <span className="ml-auto flex items-center gap-1">
                <kbd className="inline-flex h-5 items-center rounded border border-zinc-200 bg-zinc-50 px-1.5 font-mono text-[10px]">
                    ⌘K
                </kbd>
            </span>
        </button>
    );
}


function UserMenu() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    if (!user) return null;
    const initials = (user.name || user.email)
        .split(" ")
        .map((p) => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                data-testid="user-menu-trigger"
                className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-1.5 py-1 hover:bg-zinc-50 transition-colors"
            >
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900 text-white text-[10px] font-semibold">
                    {initials}
                </div>
                <div className="hidden md:flex flex-col items-start pr-1.5">
                    <span className="text-xs font-medium leading-none">{user.name || "User"}</span>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 leading-tight mt-0.5">
                        {user.role}
                    </span>
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                    <div className="text-xs font-medium truncate">{user.name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{user.email}</div>
                    <Badge variant="secondary" className="mt-1.5 text-[10px] uppercase tracking-widest">
                        {user.role}
                    </Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/app/settings/profile")}>
                    Profile
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-security" onClick={() => navigate("/app/settings/security")}>
                    Security
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/app/settings")}>
                    Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-destructive focus:text-destructive">
                    Log out
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default function Topbar({ onOpenCommand }) {
    return (
        <header
            data-testid="topbar"
            className="flex h-14 items-center gap-4 border-b border-zinc-200 bg-white/80 backdrop-blur px-6 shrink-0"
        >
            <Breadcrumbs />
            <div className="ml-auto flex items-center gap-2">
                <GlobalSearch onOpen={onOpenCommand} />
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Link
                                to="/"
                                data-testid="topbar-landing-link"
                                aria-label="View landing page"
                                className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-200 bg-white text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors"
                            >
                                <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </Link>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="text-xs">
                            View landing page
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <UserMenu />
            </div>
        </header>
    );
}
